import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin from "@fullcalendar/interaction";
import { Modal, Button } from "react-bootstrap";
import PageBody from "Common/Custom/PageBody";
import "./Styles/RoomTimeline.css";
import { RoomTimelineFilterSelect } from "adminApi/RoomStatusApi/RoomStatusApi";
import { Tooltip, OverlayTrigger } from "react-bootstrap";

// React-Bootstrap Icons
import {
  Search,
  Building,
  HouseDoor,
  CheckCircleFill,
  CalendarRange,
  ArrowRepeat,
  ChevronLeft,
  ChevronRight,
  PersonCircle,
  Envelope,
  Telephone,
  Hash,
  DoorClosed,
  CalendarCheck,
  CalendarX,
  PeopleFill,
} from "react-bootstrap-icons";

// ── date helpers ─────────────────────────────────────────────────────
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDays = (d, n) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
};

const diffDaysInclusive = (fromStr, toStr) => {
  const from = new Date(fromStr);
  const to = new Date(toStr);
  const diff = Math.round((to - from) / (1000 * 60 * 60 * 24));
  return Math.max(diff + 1, 1); // inclusive, at least 1 day
};

const diffNights = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  return Math.max(Math.round(ms / (1000 * 60 * 60 * 24)), 0);
};

const formatDate = (d) =>
  d
    ? d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "-";

const formatMoney = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Status → color tokens ─────────────────────────────────────────────
const BOOKING_STATUS_STYLE = {
  New: { bg: "#EFF6FF", text: "#1D4ED8" },
  Confirmed: { bg: "#DCFCE7", text: "#15803D" },
  Reserved: { bg: "#DBEAFE", text: "#2563EB" },
  Booked: { bg: "#EDE9FE", text: "#7C3AED" },
  Arrived: { bg: "#D1FAE5", text: "#047857" },
  Pending: { bg: "#FEF3C7", text: "#B45309" },
  CheckedOut: { bg: "#E5E7EB", text: "#4B5563" },
  Cancelled: { bg: "#FEE2E2", text: "#B91C1C" },
  Available: {
    bg: "#DCFCE7",
    text: "#15803D",
    color: "#15803D",
    bar: "#22C55E",
  },
  BookedRoom: { color: "#4F46E5", bar: "#4F46E5" },
  Cleaning: { color: "#D97706", bar: "#D97706" },
  Checkin: { color: "#0284C7", bar: "#0284C7" },
  Checkout: { color: "#6B7280", bar: "#6B7280" },
  Dirty: { color: "#DC2626", bar: "#DC2626" },
  Blocked: { color: "#991B1B", bar: "#991B1B" },
  "Do Not Reserve": { color: "#7F1D1D", bar: "#7F1D1D" },
  Occupied: { color: "#2563EB", bar: "#2563EB" },
  Ready: { color: "#16A34A", bar: "#16A34A" },
  Default: { bg: "#F3F4F6", text: "#374151" },
};

// ── Legend shown at the top of the timeline: color → meaning ─────────
const STATUS_LEGEND = [
  { label: "Available", color: BOOKING_STATUS_STYLE.Available.bar },
  { label: "Booked", color: BOOKING_STATUS_STYLE.BookedRoom.bar },
  { label: "Check-In", color: BOOKING_STATUS_STYLE.Checkin.bar },
  { label: "Check-Out", color: BOOKING_STATUS_STYLE.Checkout.bar },
  { label: "Cleaning", color: BOOKING_STATUS_STYLE.Cleaning.bar },
  { label: "Dirty", color: BOOKING_STATUS_STYLE.Dirty.bar },
  { label: "Blocked", color: BOOKING_STATUS_STYLE.Blocked.bar },
];

const getBookingCountForDate = (date, bookings) => {
  const dateStr = toYMD(date);

  console.log("bookings", bookings)

  return bookings.filter((booking) => {
    if (!booking.start || booking.accommodationType !== "ROOM") return false;

    const start = booking.start.slice(0, 10);
    const end = booking.end
      ? booking.end.slice(0, 10)
      : start;

    // Count booking on every occupied date.
    return dateStr >= start && dateStr < end;
  }).length;
};

const getBookedBedsForDate = (date, bookings) => {
  const dateStr = toYMD(date);

  return bookings.reduce((total, booking) => {
    if (
      !booking.start ||
      booking.accommodationType !== "DORMITORY" ||
      booking.bookingStatus === "Available"
    ) {
      return total;
    }

    const start = booking.start.slice(0, 10);
    const end = booking.end
      ? booking.end.slice(0, 10)
      : start;

    // Count booked beds for every occupied date
    if (dateStr >= start && dateStr < end) {
      return total + Number(booking.bookedBeds || 0);
    }

    return total;
  }, 0);
};

const RoomStatus = () => {
  const calendarRef = useRef(null);
  const [roomFilter, setRoomFilter] = useState("all");
  const [title, setTitle] = useState("");
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);

  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => toYMD(today), [today]);
  const defaultTo = useMemo(() => toYMD(addDays(today, 15)), [today]);

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);
  const [searchTerm, setSearchTerm] = useState("");

  const [appliedRange, setAppliedRange] = useState({
    from: defaultFrom,
    to: defaultTo,
  });
  const [appliedSearch, setAppliedSearch] = useState("");

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);


  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalRooms = rooms.length;

    const selectedDays = diffDaysInclusive(
      appliedRange.from,
      appliedRange.to || appliedRange.from
    );

    const totalRoomDays = totalRooms * selectedDays;

    // ---------------------------------------------------------
    // Calculate booked room-days
    // ---------------------------------------------------------
    const bookedDayCounts = bookings
      .filter(
        (booking) =>
          booking.start &&
          booking.accommodationType === "ROOM"
      )
      .map((booking) => {
        const bookingStart = booking.start.slice(0, 10);
        const bookingEnd = booking.end
          ? booking.end.slice(0, 10)
          : bookingStart;

        // Find overlap with selected date range
        const overlapStart =
          bookingStart > appliedRange.from
            ? bookingStart
            : appliedRange.from;

        const overlapEnd =
          bookingEnd < toYMD(addDays(new Date(appliedRange.to), 1))
            ? bookingEnd
            : toYMD(addDays(new Date(appliedRange.to), 1));

        const days = Math.max(
          Math.round(
            (new Date(overlapEnd) - new Date(overlapStart)) /
            (1000 * 60 * 60 * 24)
          ),
          0
        );

        return days;
      })
      .filter((days) => days > 0);

    const bookedRoomDays = bookedDayCounts.reduce(
      (sum, days) => sum + days,
      0
    );

    const availableRoomDays = Math.max(
      totalRoomDays - bookedRoomDays,
      0
    );

    const occupancy =
      totalRoomDays > 0
        ? ((bookedRoomDays / totalRoomDays) * 100).toFixed(1)
        : "0.0";

    // Example: "7 + 2 = 9"
    const bookedFormula =
      bookedDayCounts.length > 0
        ? `${bookedDayCounts.join(" + ")} = ${bookedRoomDays}`
        : `0 = 0`;

    return {
      totalRooms,
      selectedDays,
      totalRoomDays,

      bookedRoomDays,
      bookedFormula,

      availableRoomDays,
      occupancy,
    };
  }, [rooms, bookings, appliedRange]);

  const fetchData = async (from, to, search) => {
    const response = await RoomTimelineFilterSelect({ from, to, search });
    const payload = response?.data?.data;


    setRooms(payload?.rooms || []);
    setBookings(payload?.bookings || []);
  };

  useEffect(() => {
    fetchData(appliedRange.from, appliedRange.to, appliedSearch);

    const api = calendarRef.current?.getApi();
    if (api && appliedRange.from) {
      const days = diffDaysInclusive(
        appliedRange.from,
        appliedRange.to || appliedRange.from,
      );
      api.changeView("resourceTimelineRange", {
        start: appliedRange.from,
        end: toYMD(addDays(new Date(appliedRange.from), days)),
      });
      syncTitle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedRange, appliedSearch]);

  const handleSearch = () => {
    if (!fromDate) return;
    setAppliedRange({
      from: fromDate,
      to: toDate || fromDate,
    });
    setAppliedSearch(searchTerm.trim());
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleResetToDefault = () => {
    setFromDate(defaultFrom);
    setToDate(defaultTo);
    setSearchTerm("");
    setAppliedRange({ from: defaultFrom, to: defaultTo });
    setAppliedSearch("");
  };

  // Paginate the visible window by whatever span is currently selected
  // (defaults to 15 days), like Prev/Next page controls.
  const shiftRange = (direction) => {
    const span = diffDaysInclusive(fromDate, toDate || fromDate);
    const newFrom = toYMD(addDays(new Date(fromDate), direction * span));
    const newTo = toYMD(
      addDays(new Date(toDate || fromDate), direction * span),
    );
    setFromDate(newFrom);
    setToDate(newTo);
    setAppliedRange({ from: newFrom, to: newTo });
  };

  const handlePrevRange = () => shiftRange(-1);
  const handleNextRange = () => shiftRange(1);

  const resources = useMemo(
    () =>
      rooms
        .filter((r) => roomFilter === "all" || r.type === roomFilter)
        .map((r) => ({
          id: String(r.id),
          title: String(r.roomNo ?? ""),

          // Used by FullCalendar for proper numeric room ordering
          roomSort: Number(r.roomNo) || 0,

          type: r.type || "",

          extendedProps: {
            roomNo: r.roomNo,
            status: r.status,
            accommodationType: r.accommodationType,
          },
        })),
    [rooms, roomFilter],
  );

  const events = useMemo(() => {
    const roomEvents = [];

    // ---------------------------------------------------------
    // 1. NORMAL BOOKING EVENTS
    // ---------------------------------------------------------
    bookings.forEach((b) => {
      const style =
        BOOKING_STATUS_STYLE[b.bookingStatus] || BOOKING_STATUS_STYLE.Default;

      roomEvents.push({
        ...b,
        id: `booking-${b.id}`,
        resourceId: String(b.resourceId),

        backgroundColor: style.bg || style.bar || "#F3F4F6",
        borderColor: style.bg || style.bar || "#F3F4F6",
        textColor: style.text || "#374151",

        extendedProps: {
          ...b.extendedProps,
          ...b,
        },
      });
    });

    // ---------------------------------------------------------
    // 2. ROOM LEVEL BLOCKED EVENTS
    // ---------------------------------------------------------
    rooms.forEach((room) => {
      if (String(room.status).toLowerCase() !== "blocked") {
        return;
      }

      roomEvents.push({
        id: `blocked-${room.id}`,
        resourceId: String(room.id),

        // Full selected date range
        start: appliedRange.from,

        // FullCalendar end date is EXCLUSIVE
        end: toYMD(addDays(new Date(appliedRange.to), 1)),

        title: "Blocked",

        backgroundColor: BOOKING_STATUS_STYLE.Blocked.bar,
        borderColor: BOOKING_STATUS_STYLE.Blocked.bar,
        textColor: "#FFFFFF",

        extendedProps: {
          bookingStatus: "Blocked",
          roomStatus: "Blocked",
          isRoomStatusEvent: true,
          roomNo: room.roomNo,
          accommodationType: room.accommodationType,
        },
      });
    });

    return roomEvents;
  }, [bookings, rooms, appliedRange]);

  const syncTitle = () => {
    const api = calendarRef.current?.getApi();
    if (api) setTitle(api.view.title);
  };

  const handleEventClick = (info) => {
    const props = info.event.extendedProps;
    const resource = info.event.getResources()[0];

    if (props.bookingStatus === "Available") {
      return;
    }

    setSelectedBooking({
      // Actual room number
      roomNo: props.roomNo || resource?.extendedProps?.roomNo || "-",

      // Keep room ID if needed internally
      roomId: resource?.id || "-",

      guest: props.guestName || info.event.title || "-",

      status: props.bookingStatus || "-",

      checkIn: info.event.start,

      checkOut: info.event.end,

      email: props.customerEmail || "-",

      mobile: props.customerMobile || "-",

      balance: props.customerBalance,

      totalAmount: props.totalAmount,

      paidAmount: props.paidAmount,

      dueAmount: props.dueAmount,

      bookingNumber: props.bookingNumber || "-",

      isDormitory: props.accommodationType === "DORMITORY",

      totalBeds: props.totalBeds,

      bookedBeds: props.bookedBeds,
    });

    setShowBookingModal(true);
  };

  const roomTypeOptions = useMemo(() => {
    const types = new Set(rooms.map((r) => r.type).filter(Boolean));
    return Array.from(types).sort();
  }, [rooms]);

  // Derived values for the modal
  const modalStatusStyle = selectedBooking
    ? BOOKING_STATUS_STYLE[selectedBooking.status] ||
    BOOKING_STATUS_STYLE.Default
    : BOOKING_STATUS_STYLE.Default;

  const modalNights = selectedBooking
    ? diffNights(selectedBooking.checkIn, selectedBooking.checkOut)
    : null;

  const paidPercent = useMemo(() => {
    if (!selectedBooking) return 0;
    const total = Number(selectedBooking.totalAmount) || 0;
    const paid = Number(selectedBooking.paidAmount) || 0;
    if (total <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((paid / total) * 100)));
  }, [selectedBooking]);

  return (
    <PageBody card={false}>
      <div className="room-timeline-container">
        {/* ── Header card: Room Timeline title/stats row on top, filters + pagination row below ── */}
        <div className="room-timeline-card room-timeline-header-card">
          {/* Row 1: Room Timeline title + date range + stats */}
          <div className="room-timeline-header-card__summary">
            <span className="room-timeline-summary-icon" aria-hidden="true">
              <CalendarRange size={17} />
            </span>
            <div>
              <h2 className="room-timeline-summary-title">Room Timeline</h2>
              <div className="room-timeline-date-range">
                {formatDate(new Date(appliedRange.from))} -{" "}
                {formatDate(new Date(appliedRange.to))}
              </div>
            </div>

            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  {summaryStats.totalRooms} Rooms × {summaryStats.selectedDays} Days (
                  {formatDate(new Date(appliedRange.from))} To {" "}
                  {formatDate(new Date(appliedRange.to))}
                  )
                  <br />
                  {summaryStats.totalRooms} × {summaryStats.selectedDays} ={" "}
                  {summaryStats.totalRoomDays}
                </Tooltip>
              }
            >
              <div className="room-timeline-stat ms-auto">
                <div className="room-timeline-stat-value ">
                  {summaryStats.totalRoomDays}
                </div>

                <div className="room-timeline-stat-label">
                  Total Room Nights

                </div>
              </div>
            </OverlayTrigger>

            <div className="room-timeline-stat">
              <div className="room-timeline-stat-value ">
                {summaryStats.bookedRoomDays}
              </div>

              <div className="room-timeline-stat-label">
                Booked Room Nights
              </div>
            </div>


            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  {summaryStats.totalRoomDays} − {summaryStats.bookedRoomDays} ={" "}
                  {summaryStats.availableRoomDays}
                </Tooltip>
              }
            >
              <div className="room-timeline-stat">
                <div className="room-timeline-stat-value ">
                  {summaryStats.availableRoomDays}
                </div>

                <div className="room-timeline-stat-label">
                  AVAILABLE ROOM Nights
                </div>
              </div>
            </OverlayTrigger>


            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  ({summaryStats.bookedRoomDays} ÷ {summaryStats.totalRoomDays}) × 100
                </Tooltip>
              }
            >
              <div className="room-timeline-stat">
                <div className="room-timeline-stat-value ">
                  {summaryStats.occupancy}%
                </div>

                <div className="room-timeline-stat-label">
                  OCCUPANCY
                </div>
              </div>
            </OverlayTrigger>


          </div>

          {/* Row 1b: color legend (what each badge/bar color means) */}
          <div
            className="room-timeline-legend"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "14px",
              padding: "8px 16px",
              borderTop: "1px solid #F1F5F9",
            }}
          >
            {STATUS_LEGEND.map((item) => (
              <div
                key={item.label}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "999px",
                    backgroundColor: item.color,
                    display: "inline-block",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#475569" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {/* Row 2: filters + prev/next pagination */}
          <div className="room-timeline-header-card__filters">
            <div className="room-timeline-toolbar__controls">
              <div className="room-timeline-control-group">
                <label className="room-timeline-filter-label">Room Type</label>
                <select
                  className="room-timeline-filter-select"
                  value={roomFilter}
                  onChange={(e) => setRoomFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  {roomTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="room-timeline-control-group">
                <label className="room-timeline-filter-label">From Date</label>
                <input
                  type="date"
                  className="room-timeline-filter-select"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="room-timeline-control-group">
                <label className="room-timeline-filter-label">To Date</label>
                <input
                  type="date"
                  className="room-timeline-filter-select"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="room-timeline-control-group room-timeline-search-group">
                <label className="room-timeline-filter-label visually-hidden">
                  Search
                </label>
                <div className="room-timeline-search-wrapper">
                  <span
                    className="room-timeline-search-icon"
                    aria-hidden="true"
                  >
                    <Search size={15} />
                  </span>
                  <input
                    type="text"
                    className="room-timeline-search-input"
                    placeholder="Search Room, Guest, Booking Number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              </div>

              <div className="room-timeline-button-group">
                <button
                  className="room-timeline-btn room-timeline-btn--primary"
                  onClick={handleSearch}
                >
                  <Search size={14} />
                  Search
                </button>
                <button
                  className="room-timeline-btn room-timeline-btn--outline"
                  onClick={handleResetToDefault}
                >
                  <ArrowRepeat size={14} />
                  Reset
                </button>
              </div>
              <div className="room-timeline-control-group">
                <label className="room-timeline-filter-label visually-hidden">
                  Navigate
                </label>
                <div
                  className="room-timeline-nav-group"
                  role="group"
                  aria-label="Navigate date range"
                >
                  <button
                    type="button"
                    className="room-timeline-nav-btn"
                    onClick={handlePrevRange}
                    title="Previous range"
                    aria-label="Previous date range"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    className="room-timeline-nav-btn"
                    onClick={handleNextRange}
                    title="Next range"
                    aria-label="Next date range"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Calendar card: separate card, own clipped corners ── */}
        <div className="room-timeline-card room-timeline-calendar-card">
          <div className="room-timeline-calendar-wrapper">
            <FullCalendar
              ref={calendarRef}
              plugins={[resourceTimelinePlugin, interactionPlugin]}
              schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
              initialView="resourceTimelineRange"
              views={{
                resourceTimelineRange: {
                  type: "resourceTimeline",
                },
              }}
              headerToolbar={false}
              height="100vh"
              editable={false}
              selectable={false}
              resources={resources}
              events={events}
              datesSet={syncTitle}
              resourceAreaWidth="220px"
              resourceAreaHeaderContent="Rooms"
              resourceGroupField="type"
              resourceOrder="type,roomSort"
              resourcesInitiallyExpanded
              resourceGroupLabelContent={(arg) => {
                const groupRooms = rooms.filter(
                  (r) => r.type === arg.groupValue,
                );
                return (
                  <div className="room-timeline-group-header">
                    <span
                      className="room-timeline-group-icon d-inline-flex align-items-center justify-content-center text-muted"
                      aria-hidden="true"
                    >
                      <Building size={15} />
                    </span>
                    <span
                      className="room-timeline-group-name fw-semibold text-truncate"
                      title={arg.groupValue}
                    >
                      {arg.groupValue}
                    </span>
                    <span className="room-timeline-group-count text-nowrap">
                      {groupRooms.length} Rooms
                    </span>
                  </div>
                );
              }}
              resourceAreaColumns={[
                {
                  headerContent: "Number",
                  cellContent: (arg) => (
                    <span className="room-timeline-cell-number">
                      {arg.resource.title}
                    </span>
                  ),
                  width: "100%",
                },
              ]}
              slotLabelContent={(arg) => {
                const date = new Date(arg.date);

                const day = String(date.getDate()).padStart(2, "0");

                const weekday = date
                  .toLocaleDateString("en-US", {
                    weekday: "short",
                  })
                  .toUpperCase();

                const bookingCount = getBookingCountForDate(
                  date,
                  bookings
                );

                const BookedBed = getBookedBedsForDate(
                  date,
                  bookings
                );


                return (
                  <div className="room-timeline-slot-label">
                    <div className="room-timeline-slot-day">
                      {day}
                    </div>

                    <div className="room-timeline-slot-weekday">
                      {weekday}
                    </div>
                    {(bookingCount > 0 ) && (
                      <div className={`${bookingCount > 0 ? "room-timeline-slot-booking-count" : "d-none"}`}>
                        {bookingCount > 0 && `Booking ${bookingCount}`}
                      </div>
                    )}
                  </div>
                );
              }}
              eventContent={(arg) => {
                const props = arg.event.extendedProps;

                // ---------------------------------------------------------
                // ROOM LEVEL BLOCKED
                // ---------------------------------------------------------
                if (props.isRoomStatusEvent && props.roomStatus === "Blocked") {
                  return (
                    <div className="room-timeline-bar room-timeline-bar--blocked">
                      <span className="room-timeline-bar__content">
                        Blocked
                      </span>
                    </div>
                  );
                }

                // ---------------------------------------------------------
                // NORMAL BOOKING
                // ---------------------------------------------------------
                const isDorm = props.accommodationType === "DORMITORY";

                const bookingStatus = props.bookingStatus;

                const guestName = props.guestName || arg.event.title;

                const displayText = guestName;

                if (isDorm) {
                  return (
                    <div className="room-timeline-bar room-timeline-bar--dorm">
                      <span className="room-timeline-bar__beds">
                        {bookingStatus === "Available" ? (
                          <CheckCircleFill size={13} className="me-1" />
                        ) : (
                          <HouseDoor size={13} className="me-1" />
                        )}

                        {bookingStatus === "Available"
                          ? `Available ${arg.event.title}`
                          : displayText}
                      </span>
                    </div>
                  );
                }

                return (
                  <div className="room-timeline-bar">
                    <span className="room-timeline-bar__content">
                      {bookingStatus === "Available"
                        ? "Available"
                        : displayText}
                    </span>
                  </div>
                );
              }}
              eventClick={handleEventClick}
              slotDuration={{ days: 1 }}
              slotLabelInterval={{ days: 1 }}
              snapDuration={{ days: 1 }}
              slotMinWidth={100}
              stickyHeaderDates={true}
            />
          </div>
        </div>
      </div>

      {/* ── Booking Info Modal ──────────────────────────────────── */}
      <Modal
        show={showBookingModal}
        onHide={() => setShowBookingModal(false)}
        centered
      >
        <Modal.Header closeButton className="room-modal-header">
          <Modal.Title className="room-modal-title">
            <DoorClosed size={18} />
            Room {selectedBooking?.roomNo} Booking
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="room-modal-body">
          {selectedBooking && (
            <>
              {/* Status + booking number */}
              <div className="room-modal-status-row">
                <span
                  className="room-modal-status-badge"
                  style={{
                    background: modalStatusStyle.bg || "#F3F4F6",
                    color: modalStatusStyle.text || "#374151",
                  }}
                >
                  {selectedBooking.status}
                </span>
                <span className="room-modal-booking-no">
                  <Hash size={13} />
                  {selectedBooking.bookingNumber}
                </span>
              </div>

              {/* Guest info */}
              <div className="room-modal-section">
                <div className="room-modal-section-title">Guest</div>
                <div className="room-modal-info-grid">
                  <div className="room-modal-info-item">
                    <PersonCircle size={16} />
                    <div>
                      <label>Name</label>
                      <span>{selectedBooking.guest}</span>
                    </div>
                  </div>
                  <div className="room-modal-info-item">
                    <Envelope size={16} />
                    <div>
                      <label>Email</label>
                      <span>{selectedBooking.email}</span>
                    </div>
                  </div>
                  <div className="room-modal-info-item">
                    <Telephone size={16} />
                    <div>
                      <label>Mobile</label>
                      <span>{selectedBooking.mobile}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stay dates */}
              <div className="room-modal-section">
                <div className="room-modal-section-title">Stay</div>
                <div className="room-modal-stay-row">
                  <div className="room-modal-stay-item">
                    <CalendarCheck size={16} />
                    <div>
                      <label>Check-In</label>
                      <span>{formatDate(selectedBooking.checkIn)}</span>
                    </div>
                  </div>
                  <span className="room-modal-stay-arrow">→</span>
                  <div className="room-modal-stay-item">
                    <CalendarX size={16} />
                    <div>
                      <label>Check-Out</label>
                      <span>{formatDate(selectedBooking.checkOut)}</span>
                    </div>
                  </div>
                  {modalNights !== null && (
                    <span className="room-modal-stay-nights">
                      {modalNights} {modalNights === 1 ? "Night" : "Nights"}
                    </span>
                  )}
                </div>

                {selectedBooking.isDormitory && (
                  <div className="room-modal-beds">
                    <PeopleFill size={16} />
                    <span>
                      <strong>{selectedBooking.bookedBeds ?? "-"}</strong> /{" "}
                      {selectedBooking.totalBeds ?? "-"} beds booked
                    </span>
                  </div>
                )}
              </div>

              {/* Payment */}
              <div className="room-modal-section">
                <div className="room-modal-section-title">Payment</div>
                <div className="room-modal-payment-grid">
                  <div className="room-modal-payment-item">
                    <label>Total Amount</label>
                    <span>{formatMoney(selectedBooking.totalAmount)}</span>
                  </div>
                  <div className="room-modal-payment-item">
                    <label>Paid Amount</label>
                    <span className="is-paid">
                      {formatMoney(selectedBooking.paidAmount)}
                    </span>
                  </div>
                  <div className="room-modal-payment-item">
                    <label>Due Amount</label>
                    <span className="is-due">
                      {formatMoney(selectedBooking.dueAmount)}
                    </span>
                  </div>
                  <div className="room-modal-payment-item">
                    <label>Customer Balance</label>
                    <span>{formatMoney(selectedBooking.balance)}</span>
                  </div>
                </div>

                <div className="room-modal-progress-track">
                  <div
                    className="room-modal-progress-fill"
                    style={{ width: `${paidPercent}%` }}
                  />
                </div>
                <div className="room-modal-progress-caption">
                  <span>{paidPercent}% Paid</span>
                  <span>{100 - paidPercent}% Due</span>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBookingModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </PageBody>
  );
};

export default RoomStatus;
